import { Component, OnInit, OnDestroy } from '@angular/core';
import { Datasource as DatasourceCtor } from 'ngx-ui-scroll';
import { of } from 'rxjs';
import { take, catchError, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { IIncompleteEnrollmentSummary } from '../../interfaces/stats/incomplete-enrollment-summary';
import { IIncompleteEnrollmentDetails } from '../../interfaces/stats/incomplete-enrollment-details';
import { IIncompleteEnrollmentDetailsChunkResponse } from '../../interfaces/stats/incomplete-enrollment-details-chunk-response';
import { ClientStatsSectionBase } from '../client-stats-section-base';

@Component({
  selector: 'app-client-stats-incomplete-enrollments.component',
  standalone: false,
  templateUrl: './client-stats-incomplete-enrollments.component.html',
  styleUrl: './client-stats-incomplete-enrollments.component.css'
})
export class ClientStatsIncompleteEnrollmentsComponent
  extends ClientStatsSectionBase<IIncompleteEnrollmentDetails>
  implements OnInit, OnDestroy {

  // ── Incomplete Enrollments-specific state ─────────────────────────────────────────
  isSamsungPlatformEnabled = false;

  aggregationTypes: { label: string; value: string }[] = [];
  aggregationType = 'channel_platform';

  totalEnrollmentAttemptCnt = 0;
  totalPassRenderedCnt = 0;
  totalRegisteredCnt = 0;

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  override ngOnInit(): void {
    this.sortField       = 'incompleteEnrollmentId';
    this.pdfSectionTitle = 'Incomplete Enrollments';
    this.chartOptions = this.sharedService.options('Encomplete Enrollment Events by Source Channel and Platform');

    this.aggregationTypes = [
      { label: 'Channel and Platform', value: 'channel_platform' }
    ];
    this.aggregationType = 'channel_platform';

    this.summaryColumns = 
        [
          // { field: 'enrollmentDate', header: 'Enrollment Date', width: '17%', display: 'table-cell' },
          { field: 'srcChannel', header: 'Channel',   width: '20%', display: 'table-cell' },
          { field: 'platform', header: 'Platform',     width: '15%', display: 'table-cell' },
          { field: 'enrollmentAttemptCnt', header: 'Enrollment Attempts', width: '15%', display: 'table-cell' },
          { field: 'passRenderedCnt', header: 'Pass Rendered', width: '15%', display: 'table-cell' },
          { field: 'registeredCnt', header: 'Pass Registered', width: '16%', display: 'table-cell' },
        ];

    this.initDatasource();
  }

  override ngOnDestroy(): void {}

  // ── Params override — also reads Samsung flag ─────────────────────────────

  protected override onParamsChanged(key: string, params?: Record<string, any>): void {
    if (params) {
      this.isSamsungPlatformEnabled = params['isSamsungPlatformEnabled'] || false;
    }
    super.onParamsChanged(key, params);
  }

  // ── Abstract implementations ──────────────────────────────────────────────

  override isSelected(row: IIncompleteEnrollmentDetails): boolean {
    return this.selectedRow?.sessionId === row.sessionId;
  }

  onAggregationTypeChanged(_data: any): void {
    this.fetchData();
  }

  override fetchData(): void {
    this.customLoggerService.logDebug('Fetching incomplete enrollments summary - Brand:', this.selectedBrandCode,
      'Date From:', this.selectedDateFrom, 'Date To:', this.selectedDateTo);

    if (!(this.selectedBrandCode && this.selectedDateFrom && this.selectedDateTo)) {
      this.showMissingCriteriaWarning(
        'Make sure to select a brand and the date interval in the selection criteria to retrieve database records.'
      );
      return;
    }

    this.isSpinnerHidden       = false;
    this.hasData               = false;
    this.totalEnrollmentAttemptCnt = 0;
    this.totalPassRenderedCnt = 0;
    this.totalRegisteredCnt = 0;

    this.clientStatsService
      .getIncompleteEnrollmentsSummary(this.baseUrl, this.selectedBrandCode, this.selectedDateFrom, this.selectedDateTo, this.aggregationType)
      .subscribe({
        next: (result: IIncompleteEnrollmentSummary[]) => {
          this.customLoggerService.logDebug('fetchData - result:', result);
          this.isSpinnerHidden = true;

          if (result.length === 0) {
            this.summaryRows = [];
            this.chartData   = { labels: [], datasets: [] };
            this.showNoDataWarning();
            return;
          }

          // Totals
          result.forEach(r => {
            this.totalEnrollmentAttemptCnt   += Number(r.enrollmentAttemptCnt);
            this.totalPassRenderedCnt  += Number(r.passRenderedCnt);
            this.totalRegisteredCnt   += Number(r.registeredCnt);
          });
          this.summaryRows = result;

          // Chart - Group by source channel and platform
          const labels: string[] = [];
          const enrollmentAttemptsData: number[] = [];
          const passRenderedData: number[] = [];
          const registeredData: number[] = [];

          // Create unique labels combining source channel and platform
          result.forEach(r => {
            const label = `${r.srcChannel} - ${r.platform}`;
            if (labels.indexOf(label) < 0) {
              labels.push(label);
            }
          });

          // Populate data arrays for each label
          labels.forEach((label, index) => {
            const [srcChannel, platform] = label.split(' - ');
            const matchingRecord = result.find(r => r.srcChannel === srcChannel && r.platform === platform);
            
            if (matchingRecord) {
                enrollmentAttemptsData[index] = Number(matchingRecord.enrollmentAttemptCnt);
                passRenderedData[index] = Number(matchingRecord.passRenderedCnt);
              registeredData[index] = Number(matchingRecord.registeredCnt);
            } else {
                enrollmentAttemptsData[index] = 0;
                passRenderedData[index] = 0;
                registeredData[index] = 0;
            }
          });

          this.chartData = {
            labels,
            datasets: [
              { 
                label: 'Enrollment Attempts', 
                backgroundColor: '#C9512B', 
                borderColor: '#C9512B', 
                data: enrollmentAttemptsData 
              },
              { 
                label: 'Pass Rendered', 
                backgroundColor: '#2AFFA2', 
                borderColor: '#2AFFA2', 
                data: passRenderedData 
              },
              { 
                label: 'Pass Registered', 
                backgroundColor: '#2726dd', 
                borderColor: '#2726dd', 
                data: registeredData 
              }
            ]
          };

          this.hasData = true;
          this.refreshChart();
        },
        error: (error: unknown) => {
          this.isSpinnerHidden = true;
          this.customLoggerService.logError('Error fetching incomplete enrollments summary', error);
        }
      });
  }

  override initDatasource(): void {
    this.filterName$
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(value => {
        this.currentFilterName = value ?? '';
        this.pagination.clear();
        this.datasource?.adapter.reload(0);
      });

    this.datasource = new DatasourceCtor<IIncompleteEnrollmentDetails>({
      get: (index: number, count: number, success: (items: IIncompleteEnrollmentDetails[]) => void) => {
        this.customLoggerService.logDebug(`Requesting block: index=${index}, count=${count}, sort=${this.sortField} dir=${this.sortDirection}`);
        if (index < 0) { success([]); return; }

        const dir        = this.sortDirection;
        const blockSize  = count;
        const blockStart = Math.floor(index / blockSize) * blockSize;
        const startId    = this.pagination.resolveStartId(blockStart, dir, blockSize);
        this.customLoggerService.logDebug(`Resolved startId=${startId} for blockStart=${blockStart}, dir=${dir}`);

        if (startId == null) { success([]); return; }

        this.clientStatsService
          .getIncompleteEnrollmentsDetails(
            this.baseUrl, this.selectedBrandCode,
            this.selectedDateFrom, this.selectedDateTo,
            startId, blockSize, dir, this.sortField)
          .pipe(
            take(1),
            catchError((err: unknown) => {
              console.error('chunk error:', err);
              const empty: IIncompleteEnrollmentDetailsChunkResponse = {
                summaryStatistics: { totalCnt: 0, minId: 0, maxId: 0 },
                incompleteEnrollmentDescList: []
              };
              return of(empty);
            })
          )
          .subscribe((resp: IIncompleteEnrollmentDetailsChunkResponse) => {
            console.log('Received chunk:', resp);
            this.pagination.updateMeta(
              resp.summaryStatistics.totalCnt,
              resp.summaryStatistics.minId,
              resp.summaryStatistics.maxId
            );

            const rows: IIncompleteEnrollmentDetails[] = (resp.incompleteEnrollmentDescList ?? []).map(r => ({
                sessionId: r.sessionId,
                originationType: r.originationType,
                srcChannel: r.srcChannel,
                platform: r.platform,
                accountNo: r.accountNo,
                enrollmentAttemptDate: r.enrollmentAttemptDate,
                passRenderedDt: r.passRenderedDt
            }));
            console.log('Transformed rows:', rows);

            if (rows.length > 0) {
              const lastId = rows[rows.length - 1]?.sessionId ?? 0;
              this.pagination.cacheNextBlock(dir, blockStart, blockSize, lastId);
            }
            success(rows);
          });
      },
      settings: {
        startIndex: 0,
        minIndex:   0,
        bufferSize: this.pagination.bufferSize,
        padding:    this.pagination.padding
      }
    });
  }
}